import {
  FieldContext,
  PathKind,
  SchemaPath,
  SchemaPathRules,
  validate,
  ValidationError,
} from '@angular/forms/signals';
import {
  VALID_WHEN_DEFAULT_ERROR_MESSAGE,
  VALID_WHEN_DEFAULT_ERROR_PROPERTY,
  ValidWhenConfig,
} from './valid-when.config';

/**
 * Rules for {@link validWhen}: either an array of {@link ValidWhenConfig}
 * (one error per failing rule) or any single value (one default error when
 * it is falsy), computed from the field context.
 */
export type ValidWhenRules<
  TValue,
  TPathKind extends PathKind = PathKind.Root,
> = (ctx: FieldContext<TValue, TPathKind>) => ValidWhenConfig[] | unknown;

/**
 * Signal-forms replacement for `ValidWhenDirective` (Task 35882). The
 * directive is an NG_VALIDATORS validator and its selector needs
 * `formControlName` / `formControl` / `ngModel`; a signal form never reads
 * NG_VALIDATORS, so a host converted to `[formField]` must declare the rule
 * in its `form()` schema instead. The rule runs in a computed over the model,
 * so a cross-field condition re-evaluates when EITHER field changes, with no
 * `updateValueAndValidity()` call.
 *
 * Before (template-driven):
 * ```html
 * <input #start="ngModel" [(ngModel)]="m.start"
 *   [validWhen]="[{ expression: m.start < m.end, errorProperty: 'startBeforeEnd', errorMessage: 'Start must be before end' }]" />
 * <span *ngIf="start.errors?.startBeforeEnd">{{ start.errors?.startBeforeEnd.message }}</span>
 * ```
 *
 * After (signal form):
 * ```ts
 * readonly f = form(this.model, (p) => {
 *   validWhen(p.start, ({ valueOf }) => [
 *     { expression: valueOf(p.start) < valueOf(p.end), errorProperty: 'startBeforeEnd', errorMessage: 'Start must be before end' },
 *   ]);
 * });
 * ```
 * ```html
 * <input [formField]="f.start" />
 * @for (e of f.start().errors(); track e.kind) { <span>{{ e.message }}</span> }
 * ```
 */
export function validWhen<TValue, TPathKind extends PathKind = PathKind.Root>(
  path: SchemaPath<TValue, SchemaPathRules.Supported, TPathKind>,
  rules: ValidWhenRules<TValue, TPathKind>
): void {
  validate(path, (ctx) => validWhenErrors(rules(ctx)));
}

/** Maps a valid-when config to signal-forms errors (empty when valid). */
export function validWhenErrors(
  config: ValidWhenConfig[] | unknown
): ValidationError.WithoutField[] {
  if (Array.isArray(config)) {
    return (config as ValidWhenConfig[])
      .filter((cfg) => !cfg.expression)
      .map((cfg) => validWhenError(cfg.errorProperty, cfg.errorMessage));
  }

  return config ? [] : [validWhenError()];
}

function validWhenError(
  kind?: string,
  message?: string
): ValidationError.WithoutField {
  return {
    kind: kind || VALID_WHEN_DEFAULT_ERROR_PROPERTY,
    message: message || VALID_WHEN_DEFAULT_ERROR_MESSAGE,
  };
}
